import { useMemo, useCallback } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { organisationsAPI, contributorAPI, streamsAPI, projectsAPI } from '../../../services/api';
import { orgKeys, invalidateOrgStreamsProjectCaches } from '../../../query/queryKeys';
import { normalizeContributorRows } from '../../../utils/contributorChartRows';

const EMPTY_STATS = { streamCount: 0, projectCount: 0, memberCount: 0, repositoryCount: 0 };

/**
 * Org-scoped dashboard server state (TanStack Query). Supports optional batched metrics when `useBatchMetrics` is true.
 */
export function useDashboardRemoteData({
  orgId,
  activeDashboardTab,
  deploymentWindowDays,
  leadTimeWindowDays,
  churnWindowDays,
  contributorsTimeframe,
  contributorsStatus,
  showContributorsTab,
  useBatchMetrics,
}: any) {
  const queryClient = useQueryClient();

  const statsQuery = useQuery({
    queryKey: orgKeys.stats(orgId),
    queryFn: () => organisationsAPI.getStats(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const profileQuery = useQuery({
    queryKey: orgKeys.profile(orgId),
    queryFn: () => organisationsAPI.get(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const streamsQuery = useQuery({
    queryKey: orgKeys.streams(orgId),
    queryFn: () => streamsAPI.list(orgId),
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const streams = useMemo(() => {
    const list = streamsQuery.data;
    return Array.isArray(list) ? list : [];
  }, [streamsQuery.data]);

  const projectQueries = useQueries({
    queries: streams.map((s) => ({
      queryKey: orgKeys.streamProjects(orgId, s.id, { stats: false }),
      queryFn: () => projectsAPI.listByStream(orgId, s.id, { stats: false }),
      enabled: !!orgId && streams.length > 0,
      staleTime: 30_000,
    })),
  });

  const dashboardProjects = useMemo(() => {
    const results: any[] = [];
    streams.forEach((stream, i) => {
      const q = projectQueries[i];
      const list = q?.data;
      if (!Array.isArray(list)) return;
      list.forEach((p) => {
        results.push({ ...p, streamName: stream.name, streamId: stream.id });
      });
    });
    return results;
  }, [streams, projectQueries]);

  const dashboardProjectsLoading =
    streams.length > 0 && projectQueries.some((q) => q.isPending || q.isFetching);

  const reposQuery = useQuery({
    queryKey: orgKeys.repositories(orgId),
    queryFn: async () => {
      const { repositories: list } = await organisationsAPI.getRepositories(orgId);
      return Array.isArray(list) ? list : [];
    },
    enabled: !!orgId && activeDashboardTab === 'repos',
    staleTime: 60_000,
  });

  const batchPayload = useMemo(
    () => ({
      deploymentWindowDays,
      leadTimeWindowDays,
      churnWindowDays,
    }),
    [deploymentWindowDays, leadTimeWindowDays, churnWindowDays]
  );

  /** Reports tab uses independent period selectors — fetch metrics separately. */
  const useBatchForMetrics = useBatchMetrics && activeDashboardTab !== 'reports';

  const batchQuery = useQuery({
    queryKey: orgKeys.dashboardMetrics(orgId, batchPayload),
    queryFn: () =>
      organisationsAPI.getDashboardMetrics(orgId, {
        deploymentDays: deploymentWindowDays,
        leadDays: leadTimeWindowDays,
        churnDays: churnWindowDays,
        includeLabelDora: false,
      }),
    enabled: !!orgId && useBatchForMetrics,
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  });

  const doraDeploymentQuery = useQuery({
    queryKey: orgKeys.dora(orgId, deploymentWindowDays),
    queryFn: () => organisationsAPI.getDora(orgId, deploymentWindowDays),
    enabled: !!orgId && !useBatchForMetrics,
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  });

  const doraLeadQuery = useQuery({
    queryKey: orgKeys.dora(orgId, leadTimeWindowDays),
    queryFn: () => organisationsAPI.getDora(orgId, leadTimeWindowDays),
    enabled: !!orgId && !useBatchForMetrics,
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  });

  const churnQuery = useQuery({
    queryKey: orgKeys.churn(orgId, churnWindowDays),
    queryFn: () => organisationsAPI.getChurn(orgId, churnWindowDays),
    enabled: !!orgId && !useBatchForMetrics,
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  });

  const contributorsQuery = useQuery({
    queryKey: orgKeys.contributorAnalytics(orgId, contributorsTimeframe, contributorsStatus),
    queryFn: () =>
      contributorAPI.getByOrganisationAnalytics(orgId, {
        timeframe: contributorsTimeframe,
        status: contributorsStatus,
      }),
    enabled: !!orgId && showContributorsTab,
    staleTime: 60_000,
    select: (list) => normalizeContributorRows(Array.isArray(list) ? list : []),
  });

  const stats = statsQuery.data ?? EMPTY_STATS;
  const orgName = profileQuery.data?.name ?? '';

  const deploymentDora = useBatchForMetrics
    ? (batchQuery.data?.dora?.deployment?.dora ?? null)
    : (doraDeploymentQuery.data?.dora ?? null);
  const deploymentDoraMeta = useBatchForMetrics
    ? (batchQuery.data?.dora?.deployment?.meta ?? null)
    : (doraDeploymentQuery.data?.meta ?? null);
  const deploymentLoading = useBatchForMetrics
    ? batchQuery.isLoading
    : doraDeploymentQuery.isLoading;

  const leadTimeDora = useBatchForMetrics
    ? (batchQuery.data?.dora?.lead?.dora ?? null)
    : (doraLeadQuery.data?.dora ?? null);
  const leadTimeDoraMeta = useBatchForMetrics
    ? (batchQuery.data?.dora?.lead?.meta ?? null)
    : (doraLeadQuery.data?.meta ?? null);
  const leadTimeLoading = useBatchForMetrics ? batchQuery.isLoading : doraLeadQuery.isLoading;

  const churnData = useBatchForMetrics ? (batchQuery.data?.churn ?? null) : (churnQuery.data ?? null);
  const churnLoading = useBatchForMetrics ? batchQuery.isLoading : churnQuery.isLoading;

  const contributorsBarRows = useMemo(() => {
    const rows = contributorsQuery.data;
    return Array.isArray(rows) ? rows : [];
  }, [contributorsQuery.data]);
  const contributorsBarLoading = contributorsQuery.isPending;

  const invalidateAfterStructureChange = useCallback(() => {
    if (orgId) invalidateOrgStreamsProjectCaches(queryClient, orgId);
  }, [queryClient, orgId]);

  const invalidateStatsOnly = useCallback(() => {
    if (orgId) queryClient.invalidateQueries({ queryKey: orgKeys.stats(orgId) });
  }, [queryClient, orgId]);

  const invalidateProfileAndStats = useCallback(() => {
    if (!orgId) return;
    queryClient.invalidateQueries({ queryKey: orgKeys.profile(orgId) });
    queryClient.invalidateQueries({ queryKey: orgKeys.stats(orgId) });
  }, [queryClient, orgId]);

  return {
    stats,
    orgName,
    statsQuery,
    profileQuery,
    /** Alias for consumers expecting `dashboardStreams`. */
    dashboardStreams: streams,
    dashboardStreamsLoading: streamsQuery.isPending,
    dashboardProjects,
    dashboardProjectsLoading,
    dashboardRepos: reposQuery.data ?? [],
    dashboardReposLoading: reposQuery.isPending,
    deploymentDora,
    deploymentDoraMeta,
    deploymentLoading,
    leadTimeDora,
    leadTimeDoraMeta,
    leadTimeLoading,
    churnData,
    churnLoading,
    contributorsBarRows,
    contributorsBarLoading,
    invalidateAfterStructureChange,
    invalidateStatsOnly,
    invalidateProfileAndStats,
  };
}
